import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      return;
    }
    fetch(`${API}/api/emails/verify-reset-token/${token}`)
      .then((res) => res.json())
      .then((data) => {
        setTokenValid(data.valid);
        if (data.email) setUserEmail(data.email);
        setVerifying(false);
      })
      .catch(() => setVerifying(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error(language === 'es' ? 'La contrasena debe tener al menos 6 caracteres' : 'Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error(language === 'es' ? 'Las contrasenas no coinciden' : 'Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/emails/reset-password-confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Error');
      }

      setSuccess(true);
      toast.success(language === 'es' ? 'Contrasena actualizada' : 'Password updated');
    } catch (err) {
      toast.error(err.message || (language === 'es' ? 'Error al restablecer la contrasena' : 'Error resetting password'));
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-[#F5F5F7]">
        <Navbar />
        <main className="pt-40 pb-20 px-6">
          <div className="max-w-md mx-auto text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E63946] mx-auto"></div>
            <p className="mt-4 text-[#6E6E73]">
              {language === 'es' ? 'Verificando enlace...' : 'Verifying link...'}
            </p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <Navbar />
      <main className="pt-40 pb-20 px-6">
        <div className="max-w-md mx-auto">
          <div className="card-apple p-8">
            {success ? (
              <div className="text-center" data-testid="reset-success">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h1 className="text-2xl font-semibold text-[#1D1D1F] mb-3">
                  {language === 'es' ? 'Contrasena actualizada' : 'Password updated'}
                </h1>
                <p className="text-[#6E6E73] mb-6">
                  {language === 'es'
                    ? 'Tu contrasena ha sido restablecida exitosamente. Ya puedes iniciar sesion con tu nueva contrasena.'
                    : 'Your password has been successfully reset. You can now log in with your new password.'}
                </p>
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full py-6 rounded-xl bg-[#E63946] hover:bg-[#D62828] text-white font-medium"
                  data-testid="go-to-login-btn"
                >
                  {language === 'es' ? 'Ir a iniciar sesion' : 'Go to login'}
                </Button>
              </div>
            ) : !token || !tokenValid ? (
              <div className="text-center" data-testid="reset-token-invalid">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="h-8 w-8 text-red-600" />
                </div>
                <h1 className="text-2xl font-semibold text-[#1D1D1F] mb-3">
                  {language === 'es' ? 'Enlace invalido' : 'Invalid link'}
                </h1>
                <p className="text-[#6E6E73] mb-6">
                  {language === 'es'
                    ? 'Este enlace de restablecimiento es invalido o ha expirado. Solicita uno nuevo.'
                    : 'This reset link is invalid or has expired. Please request a new one.'}
                </p>
                <Link
                  to="/forgot-password"
                  className="inline-flex items-center gap-2 text-[#E63946] hover:underline font-medium"
                  data-testid="request-new-reset-link"
                >
                  {language === 'es' ? 'Solicitar nuevo enlace' : 'Request new link'}
                </Link>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <h1 className="text-2xl font-semibold text-[#1D1D1F] mb-2" data-testid="reset-password-title">
                    {language === 'es' ? 'Nueva contrasena' : 'New password'}
                  </h1>
                  {userEmail && (
                    <p className="text-[#6E6E73] text-sm">
                      {userEmail}
                    </p>
                  )}
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-[#1D1D1F] font-medium">
                      {language === 'es' ? 'Nueva contrasena' : 'New password'}
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#86868B]" />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="pl-12 pr-12 py-6 rounded-xl border-[#D2D2D7] bg-white"
                        required
                        minLength={6}
                        data-testid="reset-new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#86868B] hover:text-[#1D1D1F]"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-[#1D1D1F] font-medium">
                      {language === 'es' ? 'Confirmar contrasena' : 'Confirm password'}
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#86868B]" />
                      <Input
                        id="confirmPassword"
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                        className="pl-12 py-6 rounded-xl border-[#D2D2D7] bg-white"
                        required
                        minLength={6}
                        data-testid="reset-confirm-password-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full py-6 rounded-xl bg-[#E63946] hover:bg-[#D62828] text-white font-medium"
                    disabled={loading}
                    data-testid="reset-password-submit-btn"
                  >
                    {loading
                      ? (language === 'es' ? 'Guardando...' : 'Saving...')
                      : (language === 'es' ? 'Restablecer contrasena' : 'Reset password')}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-sm text-[#6E6E73] hover:text-[#1D1D1F]"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {language === 'es' ? 'Volver al inicio de sesion' : 'Back to login'}
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
